import React, { useEffect, useMemo, useState } from 'react';

const parseIsoDate = (isoDate) => {
  if (!isoDate || typeof isoDate !== 'string') {
    return null;
  }
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatShortDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const formatWeekRangeLabel = (week) => {
  const startDate = parseIsoDate(week?.start_date_iso);
  if (!startDate) {
    return week?.range || week?.display_range || week?.fecha_rango || week?.start_date_iso || null;
  }
  const endDate = new Date(startDate);
  endDate.setUTCDate(endDate.getUTCDate() + 6);
  const startLabel = formatShortDate(startDate);
  const endLabel = formatShortDate(endDate);
  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }
  return startLabel || endLabel || week?.range || week?.display_range || week?.fecha_rango || week?.start_date_iso || null;
};

const buildInitialSelection = (weeks, defaultWeeks = 2) => {
  if (!Array.isArray(weeks) || weeks.length === 0) {
    return new Set();
  }
  const currentWeekId = weeks[0]?.start_date_iso;
  const selection = new Set();
  if (currentWeekId) {
    selection.add(currentWeekId);
  }
  // defaultWeeks indica cuántas semanas hacia atrás sumar además de la actual
  const extra = weeks.slice(1, defaultWeeks + 1).map((week) => week.start_date_iso);
  extra.forEach((id) => selection.add(id));
  return selection;
};

const validateContinuousWeeks = (weeks, selection, minWeeks = 1) => {
  const weeksList = Array.isArray(weeks) ? weeks : [];
  if (!selection.length) {
    return 'Seleccione al menos una semana basada en datos históricos.';
  }
  if (selection.length < minWeeks) {
    return `Selecciona al menos ${minWeeks} semanas consecutivas.`;
  }
  const orderedIndexes = selection
    .map((value) => weeksList.findIndex((week) => week.start_date_iso === value))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);

  if (orderedIndexes.length !== selection.length) {
    return 'No se pudo validar la selección de semanas.';
  }

  for (let i = 1; i < orderedIndexes.length; i += 1) {
    if (orderedIndexes[i] !== orderedIndexes[i - 1] + 1) {
      return 'Las semanas seleccionadas deben ser consecutivas.';
    }
  }
  return null;
};

const buildRangeLabel = (weeks, selectionSet) => {
  if (!weeks || !weeks.length || !selectionSet || selectionSet.size === 0) {
    return null;
  }
  const indexes = Array.from(selectionSet)
    .map((value) => weeks.findIndex((week) => week.start_date_iso === value))
    .filter((index) => index >= 0);

  if (!indexes.length) {
    return null;
  }

  const earliestIndex = Math.max(...indexes);
  const latestIndex = Math.min(...indexes);
  const startWeek = weeks[earliestIndex];
  const endWeek = weeks[latestIndex];
  const startDate = parseIsoDate(startWeek?.start_date_iso);
  const endDate = parseIsoDate(endWeek?.start_date_iso);
  if (!startDate && !endDate) {
    return null;
  }
  if (endDate) {
    endDate.setUTCDate(endDate.getUTCDate() + 6);
  }
  const startLabel = startDate ? formatShortDate(startDate) : null;
  const endLabel = endDate ? formatShortDate(endDate) : null;
  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }
  return startLabel || endLabel;
};

const WeeksSelector = ({ weeks, defaultWeeks = 2, minWeeks = 2, autoMode = false, onSelectionChange }) => {
  const [selection, setSelection] = useState(() => buildInitialSelection(weeks, defaultWeeks));
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    setSelection(buildInitialSelection(weeks, defaultWeeks));
    setError(null);
  }, [weeks, defaultWeeks]);

  useEffect(() => {
    const message = validateContinuousWeeks(weeks, Array.from(selection), minWeeks);
    setError(message);
  }, [selection, weeks, minWeeks]);

  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      onSelectionChange(new Set(selection), error);
    }
  }, [selection, error, onSelectionChange]);

  const toggleWeek = (event) => {
    if (autoMode) {
      return;
    }
    const { value, checked } = event.target;
    setSelection((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(value);
      } else {
        next.delete(value);
      }
      return next;
    });
  };

  const selectedWeeks = useMemo(
    () => (weeks || []).filter((week) => selection.has(week.start_date_iso)),
    [weeks, selection]
  );

  const selectionRangeLabel = useMemo(() => buildRangeLabel(weeks, selection), [weeks, selection]);

  const summaryText = useMemo(() => {
    if (!weeks || weeks.length === 0) {
      return 'Sin semanas disponibles en el histórico.';
    }
    if (autoMode && selectionRangeLabel) {
      return `Rango automático: ${selectionRangeLabel}`;
    }
    if (selectedWeeks.length === 0) {
      return 'Elige semanas consecutivas con datos reales.';
    }
    if (selectionRangeLabel) {
      return `Rango seleccionado: ${selectionRangeLabel} (${selectedWeeks.length} semanas)`;
    }
    return `${selectedWeeks.length} semanas seleccionadas`;
  }, [weeks, selectedWeeks, autoMode, selectionRangeLabel]);

  const weekItems = useMemo(
    () =>
      (weeks || []).map((week, index) => {
        const weekId = week.start_date_iso || `week-${index}`;
        const isSelected = selection.has(weekId);
        const shortLabel = formatShortDate(parseIsoDate(week.start_date_iso));
        const label = week.label || `Semana ${index + 1}`;
        const primaryLabel = shortLabel || label;
        const rangeLabel = formatWeekRangeLabel(week) || 'Ventas históricas';

        return (
          <label className={`week-pill ${isSelected ? 'active' : ''}`} key={weekId}>
            {!autoMode ? (
              <input
                type="checkbox"
                className="week-pill__input"
                name="semanas"
                value={weekId}
                checked={isSelected}
                onChange={toggleWeek}
              />
            ) : null}
            <div className="week-pill__content">
              <div className="week-pill__row">
                <span className="week-pill__dot" aria-hidden="true" />
                <span className="week-pill__title">{primaryLabel}</span>
                <span className="week-pill__state">{isSelected ? 'Base activa' : 'Disponible'}</span>
              </div>
              <div className="week-pill__range">{rangeLabel}</div>
            </div>
          </label>
        );
      }),
    [weeks, selection, autoMode]
  );

  return (
    <section className={`weeks-ux ${isOpen ? 'open' : ''}`} aria-labelledby="semanasHeader">
      <button
        type="button"
        id="semanasHeader"
        className={`weeks-ux__header ${isOpen ? 'expanded' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls="semanasContainer"
      >
        <div className="weeks-ux__titles">
          <p className="weeks-ux__eyebrow">Semanas base</p>
          <h3 className="weeks-ux__title">&nbsp;</h3>
          <p className="weeks-ux__summary">{summaryText}</p>
        </div>
        <div className="weeks-ux__badge">
          <span className="weeks-ux__count">{selectedWeeks.length}</span>
          <span className="weeks-ux__meta">de {weeks?.length || 0}</span>
        </div>
        <span className="weeks-ux__chevron" aria-hidden="true">
          {isOpen ? '▾' : '▸'}
        </span>
      </button>
      <div
        id="semanasContainer"
        className={`weeks-ux__body ${isOpen ? 'open expanded' : ''}`}
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        {autoMode ? (
          <div className="weeks-ux__notice" style={{ marginBottom: '12px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f8fafc' }}>
            <p style={{ margin: 0, fontWeight: 600 }}>{selectionRangeLabel || 'Semanas por defecto'}</p>
          </div>
        ) : null}
        {!autoMode ? (
          weekItems.length > 0 ? (
            <div className="weeks-ux__grid">{weekItems}</div>
          ) : (
            <p className="loading-text">No hay semanas disponibles.</p>
          )
        ) : null}
        {!autoMode && error ? (
          <div className="form-error weeks-ux__error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default WeeksSelector;
